import firebase from "firebase/compat/app";
import { Field, Form, Formik } from "formik";
import React from "react";
import { Helmet } from "react-helmet";
import { CiLocationOn, CiMail } from "react-icons/ci";
import ClipLoader from "react-spinners/ClipLoader";
import * as Yup from "yup";

import { database } from "../config";
import { companyLogo } from "../constants";
import { useAppContext } from "../context/ThemeContext";
import ContactType from "../reusable-components/ContactType";
import CustomImage from "../reusable-components/Image";
import Layout from "../reusable-components/Layout";

function ContactUs() {
  const { state } = useAppContext();
  const [sending, setSending] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const ContactSchema = Yup.object().shape({
    name: Yup.string()
      .min(3, "Too Short!")
      .max(50, "Too Long!")
      .required("Required"),
    phone: Yup.string()
      .min(10, "Too Short!")
      .max(10, "Too Long!")
      .required("Required"),
    email: Yup.string().email("Invalid email").required("Required"),
    message: Yup.string().min(10, "Too Short!").required("Required"),
  });

  let isDay = state.isDay;
  if (JSON.parse(sessionStorage.getItem("isDay"))) {
    isDay = JSON.parse(sessionStorage.getItem("isDay"));
  }

  async function handleRequest(value) {
    setSending(true);

    try {
      await database.collection("contact-requests").add({
        timestamp: firebase.firestore.FieldValue.serverTimestamp(),
        name: value?.name,
        phone: value?.phone,
        email: value?.email,
        message: value?.message,
      });

      // Ensure data is saved before setting `setSending(false)`
      setSending(false);
      setSubmitted(true);
    } catch (error) {
      console.error("Error saving request:", error);
      setSending(false); // Ensure we stop sending even if there's an error
    }
  }

  const override = {
    display: "block",
    margin: "0 auto",
    borderColor: isDay ? "text-brandwhite" : "text-deepGrey",
  };

  React.useEffect(() => {
    if (submitted) {
      const submittedId = setTimeout(() => {
        setSubmitted(false);
      }, 3000);

      return () => clearTimeout(submittedId);
    }
  }, [submitted]);

  return (
    <>
      <Helmet>
        <title>Wiingr | Contact-Us</title>
      </Helmet>
      <Layout>
        <div className="flex-col h-full px-4 pt-40 pb-20 mx-auto overflow-hidden font-thin leading-5 text-center max-w-max-content-width md:pt-0">
          <div className="flex flex-col items-end h-[100vh] gap-20 md:flex-row md:justify-center md:gap-2">
            <div className="absolute top-[10%] left-auto w-full">
              <p className="text-[400px] overflow-hidden text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-pink-500 h-64 leading-none opacity-[0.05] font-thin">
                Contact
              </p>
            </div>
            <div className="w-full md:flex-[0.5] z-10 flex flex-col gap-20">
              <div className=" border-b-[0.5px] border-white/10 pb-10 lg:mr-10">
                <div className="text-xs text-white/50 w-full sm:w-[90%] lg:w-[80%]">
                  <div className="flex flex-row items-center h-fit">
                    <CustomImage
                      src={companyLogo}
                      alt=""
                      className="w-[3rem] h-[1.5rem] object-cover rounded-sm"
                    />
                    <div className="text-3xl bg-clip-text text-transparent cursor-pointer bg-gradient-to-r from-pink-500 via-yellow-400 to-purple-500 bg-[length:200%_200%] animate-gradient-move leading-10">
                      <p className="font-saira text-center">Wiingr</p>
                    </div>
                  </div>
                  <div className="ml-12 flex flex-col items-start">
                    <p className="text-sm tracking-wide text-left text-white/50">
                      Web | Mobile | AI-Powered Solutions for Businesses and
                      Startups: Our Hallmark of Innovation.
                    </p>
                    <div className="mt-3 flex flex-col items-start">
                      <span className="font-semibold text-sm">Contact:</span>
                      <div className="flex flex-row items-start gap-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                          className="size-2"
                        >
                          <path d="M2.5 3A1.5 1.5 0 0 0 1 4.5v.793c.026.009.051.02.076.032L7.674 8.51c.206.1.446.1.652 0l6.598-3.185A.755.755 0 0 1 15 5.293V4.5A1.5 1.5 0 0 0 13.5 3h-11Z" />
                          <path d="M15 6.954 8.978 9.86a2.25 2.25 0 0 1-1.956 0L1 6.954V11.5A1.5 1.5 0 0 0 2.5 13h11a1.5 1.5 0 0 0 1.5-1.5V6.954Z" />
                        </svg>
                        <span className="text-start">
                          oluwatosinisijola@wiingr.com | info.wiingr@gmail.com
                        </span>
                      </div>
                      <div className="flex flex-row items-center gap-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                          className="size-2"
                        >
                          <path
                            fillRule="evenodd"
                            d="m3.855 7.286 1.067-.534a1 1 0 0 0 .542-1.046l-.44-2.858A1 1 0 0 0 4.036 2H3a1 1 0 0 0-1 1v2c0 .709.082 1.4.238 2.062a9.012 9.012 0 0 0 6.7 6.7A9.024 9.024 0 0 0 11 14h2a1 1 0 0 0 1-1v-1.036a1 1 0 0 0-.848-.988l-2.858-.44a1 1 0 0 0-1.046.542l-.534 1.067a7.52 7.52 0 0 1-4.86-4.859Z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>+1 4372208744</span>
                      </div>
                      <p className="text-left mt-2 italic text-[10px] tracking-wide">
                        Wiingr provides businesses and startups with AI-powered
                        automation solutions to streamline operations, optimize
                        workflows, and enhance customer interactions, delivering
                        smarter and more efficient digital experiences.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full">
                <div className="flex flex-col items-start mb-10">
                  <p className="mb-3 text-4xl">Get in touch</p>
                  <p className="w-1/2 text-xs text-left text-neutral-400">
                    Got a question or a special request? Let us know! 😊
                  </p>
                </div>
                <div className="flex flex-col gap-y-2 ">
                  <ContactType
                    link="mailto:info.wiingr@gmail.com"
                    component={<CiMail />}
                    title="Email"
                    value="info.wiingr@gamil.com"
                    functionCall={() => {}}
                  />
                  <ContactType
                    link="https://www.google.com/maps/place/Toronto,+Canada"
                    component={<CiLocationOn />}
                    title="Location"
                    value="Toronto, Canada"
                    functionCall={() => {}}
                  />
                </div>
              </div>
            </div>
            <div className="w-full md:flex-[0.5] p-1 border-[0.5px] border-neutral-700 rounded-2xl bg-[rgba(250,250,250,0.01)] z-10">
              <Formik
                initialValues={{
                  name: "",
                  phone: "",
                  email: "",
                  message: "",
                }}
                validationSchema={ContactSchema}
                onSubmit={async (values, { resetForm }) => {
                  handleRequest(values);
                  resetForm();
                }}
              >
                {({ errors, touched }) => (
                  <Form className="flex flex-col gap-y-2">
                    <Field
                      id="name"
                      name="name"
                      placeholder="--- Enter your wiingr account Name ---"
                      type="text"
                      className={`p-2 border-[0.5px] placeholder:text-center focus:ring-0 focus:border-neutral-600 border-neutral-700 rounded-xl bg-[rgba(250,250,250,0.02)] text-sm ${
                        errors.name && touched.name
                          ? "border-red-900"
                          : "border-neutral-700"
                      }`}
                    />

                    <Field
                      id="email"
                      name="email"
                      placeholder="--- Enter your wiingr account Email ---"
                      type="email"
                      className={`p-2 border-[0.5px] placeholder:text-center focus:ring-0 focus:border-neutral-600 border-neutral-700 rounded-xl bg-[rgba(250,250,250,0.02)] text-sm ${
                        errors.email && touched.email
                          ? "border-red-900"
                          : "border-neutral-700"
                      }`}
                    />

                    <Field
                      id="phone"
                      name="phone"
                      placeholder="--- Enter your wiingr account Phone ---"
                      type="number"
                      className={`p-2 border-[0.5px] placeholder:text-center focus:ring-0 focus:border-neutral-600 border-neutral-700 rounded-xl bg-[rgba(250,250,250,0.02)] text-sm ${
                        errors.phone && touched.phone
                          ? "border-red-900"
                          : "border-neutral-700"
                      }`}
                    />

                    <Field name="message">
                      {({
                        field, // { name, value, onChange, onBlur }
                        form: { touched, errors }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
                        meta,
                      }) => (
                        <div>
                          <textarea
                            type="text"
                            placeholder="--- What can we help you with? 😊 ---"
                            row={50}
                            {...field}
                            className={`p-2 w-full h-64 border-[0.5px] placeholder:text-center focus:ring-0 focus:border-neutral-600 border-neutral-700 rounded-xl bg-[rgba(250,250,250,0.02)] text-sm ${
                              meta.touched && meta.error
                                ? "border-red-900"
                                : "border-neutral-700"
                            }`}
                          />
                        </div>
                      )}
                    </Field>

                    {submitted && (
                      <div>
                        <p className="text-xs text-green-600 placeholder:text-center">
                          Your request has been submitted
                        </p>
                      </div>
                    )}

                    {sending ? (
                      <ClipLoader
                        color={isDay ? "text-brandBlack" : "text-lightGrey"}
                        loading={sending}
                        cssOverride={override}
                        size={20}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                      />
                    ) : (
                      <div className="w-full mx-auto mb-1">
                        <button
                          type="submit"
                          className={`py-2 w-full px-5 rounded-lg ${
                            isDay
                              ? "bg-brandBlack text-lightGrey"
                              : "bg-lightGrey text-brandBlack"
                          }`}
                        >
                          Submit
                        </button>
                      </div>
                    )}
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
}

export default ContactUs;
